/**
 * Early checks against the availability endpoints, which answer with the code
 * and message the submit gives the same value: POST /v1/email-availability for
 * the email step and GET /v1/username-availability for the username step.
 */

import { validateStep } from './stepValidation.js';
import { getStepForField } from '../steps.js';
import { trackError } from '../analytics/signupAnalytics.js';
import { refusedAccountError } from '../refusedAccount.js';

// A slow or failed check never holds the user on the step. The submit still judges the value.
export const AVAILABILITY_CHECK_TIMEOUT_MS = 3000;

function withTimeout(promise, ms) {
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            () => { clearTimeout(timer); resolve(null); },
        );
    });
}

function currentValue(state, field) {
    return String(state[field] || '').trim();
}

// A refusal lands on `state.errors[field]` the way a submit refusal does.
function applyRefusal(state, field, check) {
    const answer = check?.answer;
    if (!answer || answer.available !== false || currentValue(state, field) !== check.value) {
        return false;
    }

    const error = { code: answer.code, message: answer.message };
    if (answer.meta) {
        error.meta = answer.meta;
    }
    state.errors = { ...state.errors, [field]: error };
    state.touched[field] = true;

    if (!check.tracked) {
        check.tracked = true;
        trackError(getStepForField(field), field, answer.code);
    }

    return true;
}

/**
 * Check the current value of a field, once per distinct value. A value the
 * step's own validator rejects sends no request.
 *
 * @param {Object} state - the wizard component
 * @param {string} field - the `errors` key, 'email' or 'username'
 * @param {function(string): Promise<{available: boolean, code: ?string, message: ?string, meta: ?Object}>} request
 * @returns {Promise<boolean>} true when the server refuses the value
 */
export async function checkAvailability(state, field, request) {
    const value = currentValue(state, field);
    if (!validateStep(getStepForField(field), state).valid) {
        return false;
    }

    if (state.availabilityChecks[field]?.value !== value) {
        const check = { value, answer: null, tracked: false, result: null };
        check.result = withTimeout(request(value).catch((error) => {
            // A refused browser token blocks the submit too, so the wizard says so at this step.
            const refused = refusedAccountError(error);
            if (refused) {
                state.errors = { ...state.errors, general: refused };
            }
            throw error;
        }), AVAILABILITY_CHECK_TIMEOUT_MS);
        state.availabilityChecks[field] = check;
    }

    // Promises are not wrapped by Alpine's proxy, so `result` identifies the check.
    const pending = state.availabilityChecks[field].result;
    const answer = await pending;
    const check = state.availabilityChecks[field];
    if (check?.result !== pending) {
        return false;
    }

    // No answer is not kept, so the next blur or Next asks again.
    if (!answer) {
        state.availabilityChecks[field] = null;

        return false;
    }

    // Set through the state so the template sees the answer.
    check.answer = answer;

    // An answer means the API took the browser token, so a refused-token error no longer applies.
    if (state.errors.general?.offersSignIn) {
        const rest = { ...state.errors };
        delete rest.general;
        state.errors = rest;
    }

    // A submit in flight gives its own verdict. The next hold on this field's step applies this one.
    if (state.isSubmitting) {
        return false;
    }

    return applyRefusal(state, field, check);
}

/**
 * Apply a refusal that already arrived for the current value, without waiting.
 *
 * @returns {boolean} true when the value is refused
 */
export function applyKnownRefusal(state, field) {
    const check = state.availabilityChecks[field];

    return Boolean(check) && check.value === currentValue(state, field) && applyRefusal(state, field, check);
}

/**
 * @returns {boolean} true when the server accepted the current value
 */
export function isKnownAvailable(state, field) {
    const check = state.availabilityChecks[field];

    return check?.answer?.available === true && check.value === currentValue(state, field);
}

/**
 * Keep a submit refusal as the answer for the value the submit sent. The
 * submit already counted it, and the next blur or Create account reuses it.
 *
 * @param {Object} state - the wizard component
 * @param {string} field - the `errors` key, 'email' or 'username'
 * @param {{code: ?string, message: ?string, meta: ?Object}} error - the submit's error for the field
 * @param {string} submitted - the field's value in the POST
 */
export function recordSubmitRefusal(state, field, error, submitted) {
    const answer = { available: false, code: error.code, message: error.message };
    if (error.meta) {
        answer.meta = error.meta;
    }

    state.availabilityChecks[field] = {
        value: String(submitted || '').trim(),
        answer,
        tracked: true,
        result: Promise.resolve(answer),
    };
}
